import AbstractSharingService from './abstract-sharing-service';
import MojitoCore from 'mojito/core';

const log = MojitoCore.logger.get('SharingService');

/**
 * Sharing service implementation.
 *
 * @class SharingService
 * @name service
 * @extends Mojito.Services.Sharing.AbstractSharingService
 * @memberof Mojito.Services.Sharing
 */
class SharingService extends AbstractSharingService {
    constructor() {
        super();
    }

    /**
     * Copies text to clipboard.
     *
     * @param {string} text - The text to copy.
     *
     * @returns {Promise} Promise that resolves when the text is copied or rejects on error.
     * @function Mojito.Services.Sharing.SharingService#copyTextToClipboard
     */
    async copyTextToClipboard(text) {
        await navigator.clipboard.writeText(text);
    }

    /**
     * Shares object via {@link: https://developer.mozilla.org/en-US/docs/Web/API/Web_Share_API|Web Share API}.
     *
     * @param {object} data - The data to share.
     *
     * @returns {Promise} Promise that resolves when the data is shared or rejects on error.
     * @function Mojito.Services.Sharing.SharingService#shareUrl
     */
    async webShare(data) {
        if (navigator.share) {
            await navigator.share(data);
        } else {
            log.warn('Web Share API not supported.');
        }
    }

    /**
     * Share an image based on base64-encoded data URL of the image.
     *
     * @param {string} dataUrl - The data URL for image.
     * @param {string} fileName - The file name for image.
     *
     * @returns {Promise} Promise that resolves when the image is shared or rejects on error.
     * @function Mojito.Modules.Services.Sharing.SharingService#shareImage
     */
    async shareImage(dataUrl, fileName) {
        const response = await fetch(dataUrl);
        const blob = await response.blob();
        const file = new File([blob], fileName, { type: blob.type });
        await this.webShare({ files: [file] });
    }
}

export default new SharingService();
